import { unwrap } from "@sagot/api-client";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../../../lib/query-keys";
import { usePermissions } from "../../../session/permissions";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceContext, useWorkspaceId } from "../../../session/workspace-context";
import { useOrganisationOptions } from "../contacts/use-contacts";
import { serviceKeys } from "../service-keys";
import { useServiceQuery } from "../use-service-query";
import { NEW_TICKET_TYPE_CATEGORY } from "./ticket-constants";
import type { TeamOut, TicketTypeOut } from "./ticket-types";

const CONFIG_STALE_MS = 5 * 60_000;
const MEMBERS_LIMIT = 200;

/** The workspace's statuses (custom labels, whether a status pauses the SLA). */
export function useTicketStatuses() {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "statuses"),
    staleTime: CONFIG_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-statuses", { signal }),
  });
}

/** Active ticket types; `customer` ones are what staff may raise for a client. */
export function useTicketTypes() {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "types-active"),
    staleTime: CONFIG_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-types", { params: { query: { status: "active" } }, signal }),
  });
}

export function raisableTypes(types: readonly TicketTypeOut[] | undefined): readonly TicketTypeOut[] {
  return (types ?? []).filter((type) => type.category === NEW_TICKET_TYPE_CATEGORY && type.status === "active" && !type.plan_locked);
}

export interface TeamOptions {
  /** Whether the viewer may list teams and members (`team.view`); without it only Me / Unassigned can be offered. */
  available: boolean;
  /** Permissions or the lists are still loading, so "My team" cannot be decided yet. */
  isPending: boolean;
  teams: readonly TeamOut[];
  members: readonly { userId: string; name: string }[];
  /** The team the viewer belongs to (the first one), for the "My team" view. */
  myTeam: TeamOut | null;
  nameOfTeam: (id: string | null) => string | null;
}

/**
 * Teams and members for the filters, the bulk "Assign" menu and "My team". Shares the members cache with the Team screen; a
 * member without `team.view` gets no calls (and no 403s) and the view and the pickers that need them are hidden.
 */
export function useTeamOptions(): TeamOptions {
  const api = useApi();
  const ws = useWorkspaceId();
  const { me } = useWorkspaceContext();
  const permissions = usePermissions();
  const allowed = permissions.can("team.view");
  const members = useQuery({
    queryKey: queryKeys.members(ws),
    enabled: allowed,
    queryFn: async () => (await unwrap(api.GET("/v1/members", { params: { query: { limit: MEMBERS_LIMIT } } }))).items,
  });
  const teams = useQuery({
    queryKey: serviceKeys.settingsTab(ws, "teams"),
    enabled: allowed,
    staleTime: CONFIG_STALE_MS,
    queryFn: () => unwrap(api.GET("/v1/teams")),
  });
  const mine = members.data?.find((member) => member.user_id === me.user.id);
  const myTeam = teams.data?.find((team) => mine?.team_ids.includes(team.id)) ?? null;
  return {
    available: allowed && members.data !== undefined && teams.data !== undefined,
    isPending: permissions.isPending || (allowed && (members.isPending || teams.isPending)),
    teams: teams.data ?? [],
    members: (members.data ?? []).filter((member) => member.status === "active").map((member) => ({ userId: member.user_id, name: member.name })),
    myTeam,
    nameOfTeam: (id) => (id ? (teams.data?.find((team) => team.id === id)?.name ?? null) : null),
  };
}

/** Organisations for the filter and the New ticket form; needs `organisations.view`. */
export function useTicketOrganisations() {
  const permissions = usePermissions();
  const allowed = permissions.can("organisations.view", { anywhere: true });
  const query = useOrganisationOptions(allowed);
  return { available: allowed && query.data !== undefined, options: (query.data?.items ?? []).map((organisation) => ({ id: organisation.id, name: organisation.name })) };
}
